import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Globe, BellRing, Server, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const WhyFleetTrackModule = () => {
    const { t } = useLanguage();

    const features = [
        {
            title: "Real-time Tracking",
            desc: "Volg uw voertuigen op de meter nauwkeurig met een verversingssnelheid van 1 seconde. Geen vertraging, geen blinde vlekken.",
            icon: Globe,
            accentBg: "bg-blue-50",
            accentText: "text-blue-600"
        },
        {
            title: "Gecertificeerde Hardware",
            desc: "Onze trackers zijn uitvoerig getest en voldoen aan alle EU-veiligheidsnormen. Zelf te installeren, met het Wialon platform.",
            icon: Shield,
            accentBg: "bg-emerald-50",
            accentText: "text-emerald-600"
        },
        {
            title: "24/7 Monitoring",
            desc: "Krijg direct meldingen bij verdachte activiteiten, diefstal of een incident. Altijd verbonden, altijd beschermd.",
            icon: BellRing,
            accentBg: "bg-purple-50",
            accentText: "text-purple-600"
        },
        {
            title: "Cloud Rapportage",
            desc: "Al uw rittenregistratie en brandstofdata veilig opgeslagen in de cloud en direct exporteerbaar naar Excel of PDF.",
            icon: Server,
            accentBg: "bg-indigo-50",
            accentText: "text-indigo-600"
        }
    ];

    // Alleen verifieerbare, operationele beloften (redesign-besluit 2026-06-11)
    const stats = [
        { label: "Besparing berekenen", value: "Gratis" },
        { label: "Zelf geïnstalleerd", value: "minuten" },
        { label: "Reactie op werkdagen", value: "24u" },
        { label: "Maandelijks opzegbaar", value: "100%" }
    ];

    return (
        <section className="py-24 bg-white overflow-hidden" aria-labelledby="why-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
                    <div className="flex-1 relative">
                        <div className="relative">
                            <h2 id="why-heading" className="text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-8">
                                Eén compleet platform <br />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                                    voor uw vloot
                                </span>
                            </h2>
                            <p className="text-xl text-slate-600 leading-relaxed max-w-xl mb-8">
                                Wij combineren bewezen hardware met intuïtieve software om ondernemers volledig te ontzorgen bij hun vlootbeheer.
                            </p>
                            <p className="text-slate-500 leading-relaxed max-w-xl mb-12">
                                Wij werken met bewezen, zelf te installeren hardware en het Wialon platform. Van ZZP tot grote transportbedrijven — wij hebben voor elke schaalgrootte een passende oplossing.
                            </p>
                            <div className="grid grid-cols-2 gap-6 mb-12">
                                {stats.map((stat, idx) => (
                                    <div key={idx} className="bg-slate-50 border border-slate-100 p-6 rounded-3xl hover:-translate-y-1 transition-all shadow-sm">
                                        <p className="text-3xl font-black text-blue-600 mb-1">{stat.value}</p>
                                        <p className="text-slate-500 font-bold text-xs uppercase tracking-wider">{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/over-ons/"
                                className="inline-flex items-center text-blue-600 font-bold hover:text-blue-800 transition-colors text-sm"
                            >
                                Meer over FleetTrack Holland →
                            </Link>
                        </div>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                className="group p-8 bg-white border border-slate-100 rounded-[2.5rem] shadow-xl shadow-slate-200/50 hover:border-blue-200 hover:shadow-2xl transition-all duration-500"
                            >
                                <div className={`w-14 h-14 rounded-2xl ${feature.accentBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
                                    <feature.icon className={`h-7 w-7 ${feature.accentText}`} aria-hidden="true" />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 mb-3">{feature.title}</h3>
                                <p className="text-slate-600 leading-relaxed text-sm">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyFleetTrackModule;
